import { metersEnabled } from '../audio/playbackProfile';
import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useAudio } from './AudioContext';
import { createLa2aNode, sendLa2aParams } from '../audio/la2aNode';
import { createReverbNode, sendReverbParams } from '../audio/reverbNode';
import { createDelayNode, sendDelayParams } from '../audio/delayNode';
import { buildEchoParams, resolveDelayTimes, resolveEchoStyle } from '../audio/delayTiming';
import { createAmpGraph } from '../audio/ampNode';
import { AMP_DEFAULTS } from '../audio/ampPresets';

const FxContext = createContext(null);

export const useFx = () => {
  const context = useContext(FxContext);
  if (!context) throw new Error('useFx must be used within an FxProvider');
  return context;
};

// ─── Default params per effect type ──────────────────────────────────────────

const DEFAULT_PARAMS = {
  eq: {
    bands: [
      { freq: 30,    gain: 0, q: 0.707, type: 'highpass',  slope: 12, enabled: false },
      { freq: 100,   gain: 0, q: 0.707, type: 'lowshelf',  slope: 12, enabled: false },
      { freq: 300,   gain: 0, q: 1.0,   type: 'peaking',   slope: 12, enabled: false },
      { freq: 1000,  gain: 0, q: 1.0,   type: 'peaking',   slope: 12, enabled: false },
      { freq: 3000,  gain: 0, q: 1.0,   type: 'peaking',   slope: 12, enabled: false },
      { freq: 8000,  gain: 0, q: 0.707, type: 'highshelf', slope: 12, enabled: false },
      { freq: 20000, gain: 0, q: 0.707, type: 'lowpass',   slope: 12, enabled: false },
      { freq: 500,   gain: 0, q: 1.0,   type: 'peaking',   slope: 12, enabled: false },
    ],
    outputGain: 0
  },
  compressor: { peakReduction: 0.3, outputGain: 0, mode: 'compress', blend: 0, emphasis: 0 },
  reverb: { mode: 'hall', mix: 0.28, preDelay: 20, size: 1.00, decay: 2.8, diffusion: 0.78, mod: 0.55, modRate: 1.00, hfDamp: 6500, bass: 1.20, inputCut: 13000, width: 1.00 },
  delay: {
    mode: 'single', character: 'tape', time: 375, syncToTempo: false,
    noteValue: '1/4', feedback: 0.40, mix: 0.28, tone: 0.72,
    groove: 0, saturation: 0.18, spread: 0.35, hpf: 0.12,
    characterAmount: 0.6, wowFlutter: 0.4, width: 1, ducking: 0, diffusion: 0,
    crossfeed: 0, pitchSemitones: 0
  },
  saturator: { drive: 0.3, character: 'soft', wet: 0.5, tone: 0.5 },
  amp: { ...AMP_DEFAULTS },
  vst: { pluginId: null, instanceId: null }
};

// ─── Utilities ────────────────────────────────────────────────────────────────

function deepSet(obj, path, value) {
  const keys = path.split('.');
  const result = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = result;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = Array.isArray(cur[keys[i]]) ? [...cur[keys[i]]] : { ...cur[keys[i]] };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return result;
}

let _fxIdCounter = 0;
function newFxId() {
  return `fx-${Date.now()}-${++_fxIdCounter}`;
}

// Clean/tape/analog feedback should not accumulate digital fold-back on every
// repeat. Lo-fi retains 2x deliberately because some alias texture is part of
// that character rather than an implementation defect.
export const delayFeedbackOversample = (character) => (
  String(character || 'clean') === 'lofi' ? '2x' : '4x'
);

// ─── EQ helpers ──────────────────────────────────────────────────────────────

function getCascadeCount(type, slope) {
  if (type === 'highpass' || type === 'lowpass' || type === 'bandpass') {
    return Math.max(1, Math.round(slope / 12));
    // 6→1, 12→1, 18→2(≈), 24→2, 36→3, 48→4, 72→6, 96→8
  }
  return 1;
}

// ─── Reverb mode presets ─────────────────────────────────────────────────────
//
// Modes are parameter sets for one engine, not separate algorithms. The
// worklet retunes live, so switching a mode or turning Size never rebuilds the
// graph and never drops audio.

export const REVERB_PRESETS = {
  hall:      { size: 1.00, decay: 2.8,  diffusion: 0.78, mod: 0.55, modRate: 1.00, hfDamp: 6500, bass: 1.20, inputCut: 13000, width: 1.00 },
  chamber:   { size: 0.72, decay: 1.6,  diffusion: 0.85, mod: 0.30, modRate: 0.85, hfDamp: 5200, bass: 1.10, inputCut: 12000, width: 0.92 },
  plate:     { size: 0.55, decay: 2.0,  diffusion: 0.90, mod: 0.25, modRate: 1.30, hfDamp: 8200, bass: 0.90, inputCut: 16000, width: 0.88 },
  room:      { size: 0.42, decay: 0.80, diffusion: 0.70, mod: 0.20, modRate: 0.90, hfDamp: 7000, bass: 1.00, inputCut: 14000, width: 0.80 },
  ambience:  { size: 0.35, decay: 0.45, diffusion: 0.60, mod: 0.15, modRate: 0.70, hfDamp: 9000, bass: 0.95, inputCut: 16000, width: 0.75 },
  cathedral: { size: 2.20, decay: 7.0,  diffusion: 0.80, mod: 0.62, modRate: 0.75, hfDamp: 4200, bass: 1.30, inputCut: 11000, width: 1.00 }
};

// Impulse-response tunings below serve only the no-AudioWorklet fallback.
const LEGACY_IR_MODE = {
  hall: '1970s', chamber: 'chamber', plate: 'plate',
  room: 'brightroom', ambience: 'brightroom', cathedral: '1970s'
};

// ─── Algorithmic reverb: 8-line FDN with mode-specific tuning ────────────────
//
// Each mode has:
//   rt60Base   — base reverb time in seconds (scaled by size × decay params)
//   delayMs    — 8 delay line lengths (prime-spaced to avoid resonances)
//   erMs/erGains — early reflection times (ms) and amplitudes
//   hfGain/lfGain — post-EQ shelving for tonal character
//   gated      — 1980s gate with fast fade
//   spring     — spring resonance oscillation

const REVERB_MODES = {
  '1970s': {
    rt60Base: 2.2,
    delayMs:  [37.2, 41.7, 44.4, 47.9, 53.3, 58.0, 63.5, 67.2],
    erMs:     [7, 14, 23, 35, 47, 62, 78, 95, 114, 135],
    erGains:  [0.85, 0.72, 0.61, 0.52, 0.44, 0.37, 0.31, 0.26, 0.21, 0.17],
    hfGain: -3.5, hfFreq: 4000, lfGain:  1.5, lfFreq: 200
  },
  '1980s': {
    rt60Base: 0.9,
    delayMs:  [21.4, 23.8, 26.7, 29.1, 32.5, 35.8, 39.2, 42.6],
    erMs:     [5, 10, 18, 28, 40, 54],
    erGains:  [0.90, 0.72, 0.55, 0.40, 0.28, 0.18],
    hfGain:  3.5, hfFreq: 5000, lfGain: -2.0, lfFreq: 300,
    gated: true, gateFadeMs: 60
  },
  'brightroom': {
    rt60Base: 0.65,
    delayMs:  [14.2, 15.9, 17.8, 19.4, 21.7, 23.9, 26.3, 28.8],
    erMs:     [4, 8, 13, 20, 29, 40],
    erGains:  [0.80, 0.65, 0.52, 0.40, 0.30, 0.22],
    hfGain:  5.5, hfFreq: 6000, lfGain: -3.0, lfFreq: 400
  },
  'plate': {
    rt60Base: 2.0,
    delayMs:  [28.6, 31.4, 34.9, 38.7, 43.2, 48.1, 52.7, 58.0],
    erMs:     [],
    erGains:  [],
    hfGain:  3.0, hfFreq: 5500, lfGain:  0.5, lfFreq: 250
  },
  'chamber': {
    rt60Base: 1.4,
    delayMs:  [24.3, 27.1, 30.4, 33.8, 37.7, 41.9, 46.5, 51.2],
    erMs:     [6, 13, 22, 34, 48, 64, 82],
    erGains:  [0.82, 0.68, 0.55, 0.44, 0.35, 0.27, 0.20],
    hfGain:  0.5, hfFreq: 4500, lfGain:  1.0, lfFreq: 200
  },
  'spring': {
    rt60Base: 1.6,
    delayMs:  [18.7, 20.5, 22.8, 25.3, 28.2, 31.4, 34.9, 38.7],
    erMs:     [3, 8, 15, 24],
    erGains:  [0.90, 0.65, 0.45, 0.28],
    hfGain: -5.0, hfFreq: 3500, lfGain:  2.5, lfFreq: 180,
    spring: true, springHz: 52, springAmt: 0.10
  }
};

// Per-line HF absorption coefficients (fc base + spread across 8 lines)
const HF_ABS = {
  '1970s':      { base: 4000, spread: 0.20 },
  '1980s':      { base: 8000, spread: 0.10 },
  'brightroom': { base: 9000, spread: 0.08 },
  'plate':      { base: 6000, spread: 0.15 },
  'chamber':    { base: 5000, spread: 0.18 },
  'spring':     { base: 3500, spread: 0.25 },
};

async function generateModeIR(actx, mode, size, decayParam) {
  const sr = actx.sampleRate;
  const mp = REVERB_MODES[LEGACY_IR_MODE[mode] || mode] || REVERB_MODES['1970s'];
  const rt60 = mp.rt60Base * Math.max(0.2, size) * Math.max(0.2, decayParam);
  const totalSamples = Math.min(Math.ceil((rt60 + 0.2) * sr), sr * 8);

  // ── 8-line FDN ─────────────────────────────────────────────────────────────
  const N = 8;
  // Delay lengths scale with room size
  const delayLengths = mp.delayMs.map(ms =>
    Math.max(2, Math.floor(ms * Math.max(0.5, size) * sr / 1000))
  );
  // Per-line feedback gain: achieves 60 dB decay in rt60 seconds
  const lineGains = delayLengths.map(d =>
    Math.pow(10, -3 * d / (rt60 * sr))
  );

  // Per-line HF absorption (prevents metallic tails — each line loses HF at different rate)
  const hfa = HF_ABS[LEGACY_IR_MODE[mode] || mode] || { base: 5000, spread: 0.15 };
  const lineHfA = Array.from({length: 8}, (_, i) =>
    Math.exp(-2 * Math.PI * hfa.base * (1 + i * hfa.spread) / sr));
  const hfStates = new Float32Array(8);

  // Normalized Hadamard 8×8 mixing matrix (flattened row-major for cache efficiency)
  const s = 1 / Math.sqrt(8);
  const H = new Float32Array([
     s, s, s, s, s, s, s, s,
     s,-s, s,-s, s,-s, s,-s,
     s, s,-s,-s, s, s,-s,-s,
     s,-s,-s, s, s,-s,-s, s,
     s, s, s, s,-s,-s,-s,-s,
     s,-s, s,-s,-s, s,-s, s,
     s, s,-s,-s,-s,-s, s, s,
     s,-s,-s, s,-s, s, s,-s
  ]);

  // Circular delay buffers + pre-allocated temp arrays (avoid GC in hot loop)
  const bufs = delayLengths.map(d => new Float32Array(d));
  const heads = new Int32Array(N);
  const reads = new Float32Array(N);
  const mixed = new Float32Array(N);

  const outL = new Float32Array(totalSamples);
  const outR = new Float32Array(totalSamples);

  // Gate state (1980s mode)
  const gateSamples = mp.gated ? Math.floor(mp.rt60Base * 0.85 * sr) : totalSamples;
  const gateDecay   = mp.gated ? Math.floor((mp.gateFadeMs || 60) * sr / 1000) : 1;

  const springModes = [
    { hz: 52,  amp: 0.10, phase: 0 },
    { hz: 87,  amp: 0.06, phase: Math.PI * 0.3 },
    { hz: 130, amp: 0.03, phase: Math.PI * 0.7 },
  ];
  const springPhases = springModes.map(m => m.phase);

  for (let t = 0; t < totalSamples; t++) {
    // Read outputs from all delay lines
    for (let i = 0; i < N; i++) reads[i] = bufs[i][heads[i]] * lineGains[i];

    // Per-line HF absorption (1st-order IIR lowpass in feedback loop)
    for (let i = 0; i < 8; i++) {
      hfStates[i] = (1 - lineHfA[i]) * reads[i] + lineHfA[i] * hfStates[i];
      reads[i] = hfStates[i];
    }

    // Hadamard mix (O(N²) but N=8 so only 64 multiply-adds per sample)
    mixed.fill(0);
    for (let i = 0; i < N; i++) {
      const row = i * N;
      let sum = 0;
      for (let j = 0; j < N; j++) sum += H[row + j] * reads[j];
      mixed[i] = sum;
    }

    // Impulse at t=0: inject into all lines (uniform broadband excitation)
    if (t === 0) {
      for (let i = 0; i < N; i++) mixed[i] += s;
    }

    // Write back to delay lines and advance heads
    for (let i = 0; i < N; i++) {
      bufs[i][heads[i]] = mixed[i];
      heads[i] = (heads[i] + 1) % delayLengths[i];
    }

    // Gate envelope (1980s gated reverb)
    let gate = 1.0;
    if (t >= gateSamples) {
      const fp = t - gateSamples;
      gate = fp < gateDecay ? (1 - fp / gateDecay) ** 2 : 0;
    }

    // Stereo output: even delay lines → L, odd → R (natural decorrelation)
    let lateL = (reads[0] + reads[2] + reads[4] + reads[6]) * 0.5;
    let lateR = (reads[1] + reads[3] + reads[5] + reads[7]) * 0.5;

    // Spring resonance — multi-mode with characteristic rising-pitch chirp ("boing")
    if (mp.spring) {
      let springOut = 0;
      springModes.forEach((m, mi) => {
        const hz = m.hz * (1 + 0.15 * Math.exp(-t * 4 / (mp.rt60Base * sr)));
        springPhases[mi] += 2 * Math.PI * hz / sr;
        const decay = Math.exp(-t * 2.5 / (mp.rt60Base * sr));
        springOut += Math.sin(springPhases[mi]) * decay * m.amp;
      });
      lateL += springOut;
      lateR -= springOut;
    }

    outL[t] = lateL * gate;
    outR[t] = lateR * gate;
  }

  // ── Early reflections: scattered timing (±25% jitter, deterministic seed for cache) ─
  let seed = 0;
  for (let ch of mode) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
  const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0xFFFFFFFF; };
  for (let r = 0; r < mp.erMs.length; r++) {
    const msL = mp.erMs[r] * (0.75 + rand() * 0.50);
    const msR = mp.erMs[r] * (0.75 + rand() * 0.50) * 1.017;
    const idxL = Math.floor(msL * sr / 1000);
    const idxR = Math.floor(msR * sr / 1000);
    const sign  = r % 2 === 0 ? 1 : -1;
    if (idxL < totalSamples) outL[idxL] += sign * mp.erGains[r];
    if (idxR < totalSamples) outR[idxR] -= sign * mp.erGains[r];
  }

  // ── Normalize to −12 dBFS peak ─────────────────────────────────────────────
  let peak = 0;
  for (let i = 0; i < totalSamples; i++) {
    if (Math.abs(outL[i]) > peak) peak = Math.abs(outL[i]);
    if (Math.abs(outR[i]) > peak) peak = Math.abs(outR[i]);
  }
  if (peak > 0) {
    const sc = 0.25 / peak;
    for (let i = 0; i < totalSamples; i++) { outL[i] *= sc; outR[i] *= sc; }
  }

  // ── Copy into AudioBuffer ───────────────────────────────────────────────────
  const irBuf = actx.createBuffer(2, totalSamples, sr);
  irBuf.copyToChannel(outL, 0);
  irBuf.copyToChannel(outR, 1);

  // ── Post-EQ: mode-specific tonal character via OfflineAudioContext ──────────
  const offCtx = new OfflineAudioContext(2, totalSamples, sr);
  const src = offCtx.createBufferSource();
  src.buffer = irBuf;

  // Sub-bass HPF: prevents muddy reverb on kick/bass content
  const subHP = offCtx.createBiquadFilter();
  subHP.type = 'highpass';
  subHP.frequency.value = 40;
  subHP.Q.value = 0.5;

  // LF warmth / thinness (mode-dependent)
  const lfShelf = offCtx.createBiquadFilter();
  lfShelf.type = 'lowshelf';
  lfShelf.frequency.value = mp.lfFreq;
  lfShelf.gain.value = mp.lfGain;

  // HF air absorption / brightness (mode-dependent)
  const hfShelf = offCtx.createBiquadFilter();
  hfShelf.type = 'highshelf';
  hfShelf.frequency.value = mp.hfFreq;
  hfShelf.gain.value = mp.hfGain;

  src.connect(subHP);
  subHP.connect(lfShelf);
  lfShelf.connect(hfShelf);
  hfShelf.connect(offCtx.destination);
  src.start(0);
  return offCtx.startRendering();
}

// ─── Saturation curve (2048-point asymmetric LUTs) ────────────────────────────

function makeSatCurve(drive, character) {
  const N = 2048;
  const c = new Float32Array(N);
  if (character === 'soft') {
    // Triode-style asymmetric tanh (bias=0.04 → ~15% 2nd harmonic)
    const k = 1 + drive * 12, bias = 0.04;
    const norm = Math.tanh(k * (1 + bias));
    for (let i = 0; i < N; i++) {
      const x = (i / (N - 1)) * 2 - 1;
      const xb = Math.max(-1, Math.min(1, x + bias * (1 - drive * 0.5)));
      c[i] = norm > 0 ? Math.tanh(k * xb) / norm : xb;
    }
    let dc = 0; for (let i = 0; i < N; i++) dc += c[i]; dc /= N;
    for (let i = 0; i < N; i++) c[i] -= dc;
  } else if (character === 'warm') {
    // Class-A transformer style (x/(1+|x|) type, bias=0.10 → strong 2nd harmonic)
    const k = 1 + drive * 20, bias = 0.10 + drive * 0.06;
    for (let i = 0; i < N; i++) {
      const x = (i / (N - 1)) * 2 - 1, xb = x + bias;
      c[i] = xb > 0 ? xb / (1 + xb * k * 0.5) : Math.tanh(k * xb * 0.7);
    }
    let peak = 0; for (let i = 0; i < N; i++) { const a = Math.abs(c[i]); if (a > peak) peak = a; }
    if (peak > 0) for (let i = 0; i < N; i++) c[i] /= peak;
    let dc = 0; for (let i = 0; i < N; i++) dc += c[i]; dc /= N;
    for (let i = 0; i < N; i++) c[i] -= dc;
  } else {
    // hard: Arctangent clipper + asymmetric bias (odd+even harmonics)
    const k = 1 + drive * 40, bias = 0.06;
    for (let i = 0; i < N; i++) {
      const x = (i / (N - 1)) * 2 - 1;
      const kx = Math.max(-1, Math.min(1, x * k));
      const clipped = (2 / Math.PI) * Math.atan(kx * Math.PI * 0.5);
      c[i] = Math.max(-1, Math.min(1, clipped + bias * kx * kx * Math.sign(x)));
    }
    let dc = 0; for (let i = 0; i < N; i++) dc += c[i]; dc /= N;
    for (let i = 0; i < N; i++) c[i] -= dc;
  }
  return c;
}

// ─── Asymmetric tube saturation curve (LA-2A 6AN8 triode) ────────────────────

function makeTubeCurve(drive) {
  const n = 512;
  const c = new Float32Array(n);
  const k = drive * 40;
  const bias = 0.08; // asymmetry → even harmonics (2nd harmonic warmth)
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / (n - 1) - 1;
    const xb = x + bias;
    const sat = Math.tanh(k * xb) / Math.tanh(k * (1 + bias));
    const dcComp = Math.tanh(k * bias) / Math.tanh(k * (1 + bias));
    c[i] = sat - dcComp;
  }
  return c;
}

// ─── Structure fingerprint — only topology-changing params are included ───────
// Normal param changes (levels, frequencies, mix) are handled by applyParamToNode
// and must NOT trigger a full chain rebuild.

function getStructureKey(slots, liveChain = null) {
  return (slots || [])
    .filter(s => s.enabled)
    .map(s => {
      let key = `${s.id}:${s.type}`;
      const p = s.params;
      if (s.type === 'reverb') {
        // Every reverb control retunes the worklet in place, so no reverb
        // parameter belongs in the rebuild key.
      } else if (s.type === 'delay') {
        // The worklet retunes every control in place. The native Web Audio
        // fallback still has a different graph for each mode/style, so include
        // those fields only when that fallback is the live implementation.
        const liveSlot = liveChain?.slots?.find(entry => entry.slotId === s.id);
        if (liveSlot && !liveSlot.internalNodes?.echoNode) {
          key += `:${p.mode || 'single'}:${resolveEchoStyle(p.character)}`;
        }
      } else if (s.type === 'eq') {
        // Cascade count depends on type and slope per band
        key += `:${(p.bands || []).map(b => `${b.type}:${b.slope}:${b.enabled ? 1 : 0}`).join('|')}`;
      } else if (s.type === 'saturator') {
        // WaveShaper curve depends on character
        key += `:${p.character}`;
      } else if (s.type === 'amp') {
        key += `:${p.model || AMP_DEFAULTS.model}`;
      } else if (s.type === 'vst') {
        // Rebuild when the loaded plugin changes
        key += `:${p.pluginId || 'none'}`;
      }
      // compressor: all params handled live by applyParamToNode, no rebuild needed
      return key;
    })
    .join(',');
}

// ─── Teardown helpers ─────────────────────────────────────────────────────────

function teardownFxNodes(entry) {
  if (!entry) return;
  try { entry.inputGain.disconnect(); } catch (_) {}
  try { entry.outputGain.disconnect(); } catch (_) {}
  if (entry.internalNodes) {
    for (const node of Object.values(entry.internalNodes)) {
      if (node && typeof node.stop === 'function') { try { node.stop(); } catch (_) {} }
      if (node && typeof node.disconnect === 'function') { try { node.disconnect(); } catch (_) {} }
    }
  }
}

const FX_GRAPH_CROSSFADE_SECONDS = 0.02;

function teardownFxChain(chain) {
  if (!chain) return;
  if (chain.cleanupTimer) window.clearTimeout(chain.cleanupTimer);
  try { chain.sourceInput?.disconnect(chain.inputTarget); } catch (_) {}
  (chain.slots || []).forEach(teardownFxNodes);
  try { chain.outputGate?.disconnect(); } catch (_) {}
}

function rampFxGate(param, target, now, endTime) {
  if (!param) return;
  try {
    if (typeof param.cancelAndHoldAtTime === 'function') param.cancelAndHoldAtTime(now);
    else {
      const held = Number(param.value);
      param.cancelScheduledValues(now);
      param.setValueAtTime(Number.isFinite(held) ? held : (target > 0 ? 0 : 1), now);
    }
    param.linearRampToValueAtTime(target, endTime);
  } catch (_) {
    param.value = target;
  }
}

// ─── Provider ────────────────────────────────────────────────────────────────

export const FxProvider = ({ children }) => {
  const { audioContext, tracks, drumTracks, buses, updateTrack, trackNodesRef, bpm, registerTrackFxLatency, registerOfflineFxBuilder } = useAudio();

  const [trackFxMap, setTrackFxMap] = useState({}); // { [trackId]: FxSlot[] }
  const [fxChainStatus, setFxChainStatus] = useState({});
  const trackFxMapRef = useRef(trackFxMap);
  trackFxMapRef.current = trackFxMap;
  const fxNodesMapRef = useRef(new Map());           // trackId → { slots: FxNodeEntry[] }
  const irCacheRef = useRef(new Map());              // key → { buffer, timestamp }
  const fxStructureRef = useRef({});                 // trackId → last structureKey (skip rebuild if unchanged)
  const pendingFxRef = useRef(new Map());
  const savedChannelsRef = useRef([]);
  savedChannelsRef.current = [...(tracks || []), ...(drumTracks || []), ...(buses || [])];
  const rebuildControlRef = useRef({});              // trackId → { cancelled } (abort stale async rebuilds)

  // ─── IR cache helper ──────────────────────────────────────────────────────

  const getCachedIR = useCallback(async (actx, mode, size, decay) => {
    const cacheKey = `${mode}:${size.toFixed(2)}:${decay.toFixed(2)}:${actx.sampleRate}`;
    if (irCacheRef.current.has(cacheKey)) {
      return irCacheRef.current.get(cacheKey).buffer;
    }
    const buffer = await generateModeIR(actx, mode, size, decay);
    // Evict oldest if > 20
    if (irCacheRef.current.size >= 20) {
      const oldestKey = irCacheRef.current.keys().next().value;
      irCacheRef.current.delete(oldestKey);
    }
    irCacheRef.current.set(cacheKey, { buffer, timestamp: Date.now() });
    return buffer;
  }, []);

  // ─── Build effect subgraph ─────────────────────────────────────────────────

  // `targetContext` is an OfflineAudioContext during export, so a bounce runs
  // the same effect graphs the mixer plays rather than a second approximation.
  const buildEffectSubgraph = useCallback(async (slot, targetContext = null) => {
    const actx = targetContext || audioContext;
    const inputGain = actx.createGain();
    const outputGain = actx.createGain();
    const p = slot.params;
    const internalNodes = {};

    if (slot.type === 'eq') {
      // 8 bands, each with cascaded filters, stored as flat keys band_N_filter_J
      const allNodes = [];
      for (let i = 0; i < 8; i++) {
        const band = p.bands[i];
        const count = getCascadeCount(band.type, band.slope);
        for (let j = 0; j < count; j++) {
          const f = actx.createBiquadFilter();
          f.type = band.type;
          if (band.enabled) {
            f.frequency.value = band.freq;
            f.gain.value = band.gain;
            f.Q.value = band.q;
          } else {
            if (band.type === 'highpass') {
              f.frequency.value = 1;
            } else if (band.type === 'lowpass') {
              f.frequency.value = 22050;
            } else {
              f.frequency.value = band.freq;
              f.gain.value = 0;
              f.Q.value = band.q;
            }
          }
          internalNodes[`band_${i}_filter_${j}`] = f;
          allNodes.push(f);
        }
      }
      // Create EQ output gain and post-EQ analyser
      const eqOutputGainNode = actx.createGain();
      eqOutputGainNode.gain.value = Math.pow(10, (p.outputGain || 0) / 20);
      const postAnalyser = actx.createAnalyser();
      postAnalyser.fftSize = 2048;
      postAnalyser.smoothingTimeConstant = 0.8;
      internalNodes.eqOutputGain = eqOutputGainNode;
      internalNodes.postAnalyser = postAnalyser;

      // Wire all in series: bands → eqOutputGain → postAnalyser → outputGain
      inputGain.connect(allNodes[0]);
      for (let i = 0; i < allNodes.length - 1; i++) allNodes[i].connect(allNodes[i + 1]);
      allNodes[allNodes.length - 1].connect(eqOutputGainNode);
      eqOutputGainNode.connect(postAnalyser);
      postAnalyser.connect(outputGain);

    } else if (slot.type === 'compressor') {
      // LA-2A optical/tube model. Rust/WASM performs the real-time DSP inside
      // an AudioWorklet; the existing native-node graph remains a resilient
      // fallback for browsers that cannot initialize the worklet or WASM.
      const isLimit = p.mode === 'limit';
      const PR = p.peakReduction; // 0–1

      // Input analyser for the panel level lamp and fallback diagnostics.
      const inputAnalyser = actx.createAnalyser();
      inputAnalyser.fftSize = 256;
      inputAnalyser.smoothingTimeConstant = 0;

      const la2aNode = await createLa2aNode(actx, p);
      if (la2aNode) {
        // Meter reports are main-thread messages; send none where no meter is drawn.
        if (targetContext || !metersEnabled()) la2aNode.port.postMessage({ type: 'metering', enabled: false });
        const la2aMeter = { reductionDb: 0, inputDb: -Infinity, outputDb: -Infinity, backend: 'starting' };
        la2aNode.port.onmessage = (event) => {
          if (event.data?.type === 'meter') {
            la2aMeter.reductionDb = Number(event.data.reductionDb) || 0;
            la2aMeter.inputDb = Number.isFinite(event.data.inputDb) ? event.data.inputDb : -Infinity;
            la2aMeter.outputDb = Number.isFinite(event.data.outputDb) ? event.data.outputDb : -Infinity;
          } else if (event.data?.type === 'backend') {
            la2aMeter.backend = event.data.backend || 'unknown';
          }
        };
        internalNodes.inputAnalyser = inputAnalyser;
        internalNodes.la2aNode = la2aNode;
        internalNodes.la2aMeter = la2aMeter;
        inputGain.connect(inputAnalyser);
        inputAnalyser.connect(la2aNode);
        la2aNode.connect(outputGain);
      } else {

      // Pre-HPF: attenuates sub-bass detection (matches real LA-2A cell behavior)
      const preHPF = actx.createBiquadFilter();
      preHPF.type = 'highpass';
      preHPF.frequency.value = isLimit ? 120 : 72;
      preHPF.Q.value = 0.5;

      // comp1 — fast opto stage (catches transients)
      const comp1 = actx.createDynamicsCompressor();
      comp1.threshold.value = isLimit ? -12 * PR : -24 * PR;
      comp1.ratio.value = isLimit ? 20 : 3;
      comp1.knee.value = isLimit ? 2 : 8;
      comp1.attack.value = isLimit ? 0.003 : 0.008;
      comp1.release.value = isLimit ? 0.060 : 0.120;

      // comp2 — slow opto stage (program-dependent release tails)
      const comp2 = actx.createDynamicsCompressor();
      comp2.threshold.value = isLimit ? -6 * PR : -18 * PR;
      comp2.ratio.value = isLimit ? 6 : 1.5;
      comp2.knee.value = isLimit ? 1 : 6;
      comp2.attack.value = isLimit ? 0.015 : 0.080;
      comp2.release.value = isLimit ? 0.300 : 0.800;

      // Tube saturation — asymmetric tanh (6AN8 triode even harmonics)
      const drive = isLimit ? 0.05 + PR * 0.07 : 0.03 + PR * 0.09;
      const tubeSat = actx.createWaveShaper();
      tubeSat.oversample = '4x';
      tubeSat.curve = makeTubeCurve(drive);

      // Warm EQ — output transformer bloom
      const warmEQ = actx.createBiquadFilter();
      warmEQ.type = 'lowshelf';
      warmEQ.frequency.value = 280;
      warmEQ.gain.value = isLimit ? 0.5 : 0.8;

      // Makeup gain + parallel blend
      const blend = p.blend || 0;
      const makeupGain = actx.createGain();
      makeupGain.gain.value = (1 - blend) * Math.pow(10, (p.outputGain || 0) / 20);
      const parallelMerge = actx.createGain();
      parallelMerge.gain.value = 1;
      const parallelDryGain = actx.createGain();
      parallelDryGain.gain.value = blend;

      internalNodes.inputAnalyser = inputAnalyser;
      internalNodes.preHPF = preHPF;
      internalNodes.comp1 = comp1;
      internalNodes.comp2 = comp2;
      internalNodes.tubeSat = tubeSat;
      internalNodes.warmEQ = warmEQ;
      internalNodes.makeupGain = makeupGain;
      internalNodes.parallelMerge = parallelMerge;
      internalNodes.parallelDryGain = parallelDryGain;

      // inputGain → inputAnalyser → preHPF → comp1 → comp2 → tubeSat → warmEQ → makeupGain → parallelMerge → outputGain
      // inputGain → parallelDryGain → parallelMerge (parallel dry blend)
      inputGain.connect(inputAnalyser);
      inputAnalyser.connect(preHPF);
      preHPF.connect(comp1);
      comp1.connect(comp2);
      comp2.connect(tubeSat);
      tubeSat.connect(warmEQ);
      warmEQ.connect(makeupGain);
      makeupGain.connect(parallelMerge);
      inputGain.connect(parallelDryGain);
      parallelDryGain.connect(parallelMerge);
      parallelMerge.connect(outputGain);
      }

    } else if (slot.type === 'reverb') {
      // Modulated hall reverb. Rust/WASM runs a Dattorro-style figure-eight
      // tank inside an AudioWorklet so the tail keeps moving. The convolution
      // path below cannot do that — convolution is time-invariant, so its tail
      // is frozen the moment the impulse response is rendered.
      const reverbNode = await createReverbNode(actx, p);
      if (reverbNode) {
        internalNodes.reverbNode = reverbNode;
        reverbNode.port.onmessage = (event) => {
          if (event.data?.type === 'backend') internalNodes.reverbBackend = event.data.backend;
        };
        // The worklet applies its own wet/dry balance, so it sits inline.
        inputGain.connect(reverbNode);
        reverbNode.connect(outputGain);
      } else {
      // Fallback for browsers without AudioWorklet support.
      const preDelayNode = actx.createDelay(0.25); // max 250 ms
      preDelayNode.delayTime.value = Math.max(0, (p.preDelay || 0)) / 1000;

      // Pre-HPF: prevents sub-bass smearing into the reverb tail
      const preHP = actx.createBiquadFilter();
      preHP.type = 'highpass';
      preHP.frequency.value = 80;
      preHP.Q.value = 0.5;

      const convolver = actx.createConvolver();
      convolver.normalize = false; // IR is already normalized to −12 dBFS
      const irBuffer = await getCachedIR(actx, p.mode, p.size, p.decay);
      convolver.buffer = irBuffer;

      // HF damp: user-controlled high-frequency roll-off on the wet signal
      const hfDampFilter = actx.createBiquadFilter();
      hfDampFilter.type = 'lowpass';
      hfDampFilter.frequency.value = p.hfDamp;

      // Modulation LFO: subtle pitch shimmer on the tail (Valhalla-style)
      const modDelay = actx.createDelay(0.05);
      modDelay.delayTime.value = 0.008;

      const lfoOsc = actx.createOscillator();
      lfoOsc.type = 'sine';
      lfoOsc.frequency.value = 0.4 + p.mod * 1.8;

      const lfoGain = actx.createGain();
      lfoGain.gain.value = p.mod * 0.004;

      lfoOsc.connect(lfoGain);
      lfoGain.connect(modDelay.delayTime);
      lfoOsc.start();

      const wetGain = actx.createGain();
      wetGain.gain.value = p.mix;

      const dryGain = actx.createGain();
      dryGain.gain.value = 1 - p.mix;

      internalNodes.preDelayNode = preDelayNode;
      internalNodes.preHP = preHP;
      internalNodes.convolver = convolver;
      internalNodes.hfDampFilter = hfDampFilter;
      internalNodes.modDelay = modDelay;
      internalNodes.osc_mod = lfoOsc;
      internalNodes.lfoGain = lfoGain;
      internalNodes.wetGain = wetGain;
      internalNodes.dryGain = dryGain;

      // Dry path
      inputGain.connect(dryGain);
      dryGain.connect(outputGain);

      // Wet path: preDelay → preHP → convolver → hfDamp → modDelay → wet → out
      inputGain.connect(preDelayNode);
      preDelayNode.connect(preHP);
      preHP.connect(convolver);
      convolver.connect(hfDampFilter);
      hfDampFilter.connect(modDelay);
      modDelay.connect(wetGain);
      wetGain.connect(outputGain);
      }

    } else if (slot.type === 'delay') {
      // Character echo. The Rust worklet keeps its colouring chain inside the
      // feedback loop, so repeats degrade as they recirculate, and every
      // control retunes in place instead of rebuilding the node graph.
      const echoNode = await createDelayNode(actx, buildEchoParams(p, bpm));
      if (echoNode) {
        internalNodes.echoNode = echoNode;
        echoNode.port.onmessage = (event) => {
          if (event.data?.type === 'backend') internalNodes.echoBackend = event.data.backend;
        };
        // The worklet applies its own wet/dry balance, so it sits inline.
        inputGain.connect(echoNode);
        echoNode.connect(outputGain);
      } else {
      // ─── Fallback for browsers without AudioWorklet support ───────────────

      // Compute base delay time
      let baseTime = (p.time || 375) / 1000;
      if (p.syncToTempo && bpm) {
        const divMap = { '1/2': 2, '1/2T': 4/3, '1/2D': 3, '1/4': 1, '1/4T': 2/3, '1/4D': 1.5,
                         '1/8': 0.5, '1/8T': 1/3, '1/8D': 0.75, '1/16': 0.25, '1/16T': 1/6, '1/16D': 0.375 };
        baseTime = ((divMap[p.noteValue] || 1) / bpm) * 60;
      }
      if (p.groove !== 0 && bpm) {
        baseTime += p.groove * 0.25 * (0.25 / bpm * 60);
      }
      baseTime = Math.max(0.001, Math.min(baseTime, 5.0));

      // Character profiles: HPF floor, LPF ceiling, LFO (pitch wobble), sat scale
      const CHAR_PROFILE = {
        digital:{ hpf: 20,  lpfMax: 19000, lfoFreq: 0,   lfoDepth: 0,      satScale: 0.0  },
        clean:  { hpf: 20,  lpfMax: 19000, lfoFreq: 0,   lfoDepth: 0,      satScale: 0.0  },
        tape:   { hpf: 120, lpfMax: 7500,  lfoFreq: 0.7, lfoDepth: 0.0012, satScale: 0.6  },
        analog: { hpf: 150, lpfMax: 5500,  lfoFreq: 1.8, lfoDepth: 0.0004, satScale: 0.7  },
        lofi:   { hpf: 200, lpfMax: 3000,  lfoFreq: 3.5, lfoDepth: 0.0020, satScale: 1.5  },
        telephone: { hpf: 300, lpfMax: 3400, lfoFreq: 0.2, lfoDepth: 0.0001, satScale: 1.1 },
        fuzz:   { hpf: 80,  lpfMax: 4200,  lfoFreq: 0.8, lfoDepth: 0.0006, satScale: 1.8  },
      };
      const style = resolveEchoStyle(p.character);
      const cp = CHAR_PROFILE[style] || CHAR_PROFILE.tape;

      // Tone: 0 → cp.hpf×2 Hz, 1 → cp.lpfMax Hz (logarithmic within character range)
      const toneMin = cp.hpf * 2;
      const toneCutoff = toneMin * Math.pow(cp.lpfMax / toneMin, p.tone ?? 0.72);

      // Input HPF: p.hpf 0→20Hz, 1→500Hz
      const inputHpfFreq = 20 * Math.pow(25, p.hpf ?? 0.12);
      const spread = Math.max(0, Math.min(1, p.width ?? p.spread ?? 0.35));

      // Shared wet/dry gains
      const wetGain = actx.createGain();
      wetGain.gain.value = p.mix;
      const dryGain = actx.createGain();
      dryGain.gain.value = 1 - p.mix;
      internalNodes.wetGain = wetGain;
      internalNodes.dryGain = dryGain;
      inputGain.connect(dryGain);
      dryGain.connect(outputGain);

      // Helper: build character feedback path (HPF→LPF→Sat) for one channel
      const makeFeedPath = (suffix) => {
        const fHPF = actx.createBiquadFilter();
        fHPF.type = 'highpass';
        fHPF.frequency.value = cp.hpf;
        fHPF.Q.value = 0.5;

        const fLPF = actx.createBiquadFilter();
        fLPF.type = 'lowpass';
        fLPF.frequency.value = toneCutoff;
        fLPF.Q.value = 0.5;

        const fSat = actx.createWaveShaper();
        fSat.oversample = delayFeedbackOversample(style);
        fSat.curve = makeSatCurve((p.saturation ?? 0.18) * cp.satScale,
                                  style === 'lofi' ? 'hard' : 'soft');
        fHPF.connect(fLPF);
        fLPF.connect(fSat);
        internalNodes[`feedHPF${suffix}`] = fHPF;
        internalNodes[`feedLPF${suffix}`] = fLPF;
        internalNodes[`feedSat${suffix}`] = fSat;
        return { in: fHPF, out: fSat };
      };

      // Helper: attach pitch-wobble LFO to a delay node
      const addLFO = (delayNode, suffix) => {
        if (cp.lfoFreq === 0) return;
        const lfo = actx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.value = cp.lfoFreq;
        const lfoG = actx.createGain();
        lfoG.gain.value = cp.lfoDepth * (p.wowFlutter ?? 0.4);
        lfo.connect(lfoG);
        lfoG.connect(delayNode.delayTime);
        lfo.start();
        internalNodes[`lfo${suffix}`] = lfo;
        internalNodes[`lfoGain${suffix}`] = lfoG;
        // Flutter: 8.2Hz mechanical component (tape/lofi characters only)
        if (style === 'tape' || style === 'lofi') {
          const lfoFlutter = actx.createOscillator();
          lfoFlutter.type = 'sine';
          lfoFlutter.frequency.value = 8.2;
          const lfoFlutterG = actx.createGain();
          lfoFlutterG.gain.value = cp.lfoDepth * (p.wowFlutter ?? 0.4) * 0.15;
          lfoFlutter.connect(lfoFlutterG);
          lfoFlutterG.connect(delayNode.delayTime);
          lfoFlutter.start();
          internalNodes[`lfoFlutter${suffix}`] = lfoFlutter;
          internalNodes[`lfoFlutterGain${suffix}`] = lfoFlutterG;
        }
      };

      // Input HPF (shared across modes)
      const inputHPF = actx.createBiquadFilter();
      inputHPF.type = 'highpass';
      inputHPF.frequency.value = inputHpfFreq;
      inputHPF.Q.value = 0.5;
      internalNodes.inputHPF = inputHPF;
      inputGain.connect(inputHPF);

      if (p.mode === 'single') {
        const delay = actx.createDelay(5.0);
        delay.delayTime.value = baseTime;
        addLFO(delay, '');
        const feed = makeFeedPath('');
        const feedbackGain = actx.createGain();
        feedbackGain.gain.value = Math.min(p.feedback, 0.95);
        internalNodes.delay = delay;
        internalNodes.feedbackGain = feedbackGain;

        // Haas stereo spread: direct → panL, via short delay → panR
        const panL = actx.createStereoPanner();
        panL.pan.value = -spread;
        const haasDelay = actx.createDelay(0.05);
        haasDelay.delayTime.value = spread * 0.012; // 0..12ms
        const panR = actx.createStereoPanner();
        panR.pan.value = spread;
        internalNodes.panL = panL;
        internalNodes.haasDelay = haasDelay;
        internalNodes.panR = panR;

        inputHPF.connect(delay);
        delay.connect(feed.in);
        feed.out.connect(feedbackGain);
        feedbackGain.connect(delay);       // feedback loop
        feed.out.connect(panL);            // wet L
        feed.out.connect(haasDelay);       // Haas offset
        haasDelay.connect(panR);           // wet R
        panL.connect(wetGain);
        panR.connect(wetGain);
        wetGain.connect(outputGain);

      } else if (p.mode === 'dual') {
        // Two independent delay lines: L=baseTime, R=baseTime×1.47 (avoids comb)
        const delayL = actx.createDelay(5.0);
        delayL.delayTime.value = baseTime;
        const delayR = actx.createDelay(5.0);
        delayR.delayTime.value = Math.min(baseTime * 1.47, 5.0);
        addLFO(delayL, '_L');
        addLFO(delayR, '_R');

        const feedL = makeFeedPath('_L');
        const feedR = makeFeedPath('_R');

        const fbGainL = actx.createGain();
        fbGainL.gain.value = Math.min(p.feedback, 0.95);
        const fbGainR = actx.createGain();
        fbGainR.gain.value = Math.min(p.feedback * 0.9, 0.95);
        internalNodes.delayL = delayL;
        internalNodes.delayR = delayR;
        internalNodes.feedbackGainL = fbGainL;
        internalNodes.feedbackGainR = fbGainR;

        const panL = actx.createStereoPanner();
        panL.pan.value = -(0.5 + spread * 0.5);
        const panR = actx.createStereoPanner();
        panR.pan.value = (0.5 + spread * 0.5);
        internalNodes.panL = panL;
        internalNodes.panR = panR;

        inputHPF.connect(delayL);
        inputHPF.connect(delayR);
        delayL.connect(feedL.in);
        feedL.out.connect(fbGainL);
        fbGainL.connect(delayL);
        feedL.out.connect(panL);
        delayR.connect(feedR.in);
        feedR.out.connect(fbGainR);
        fbGainR.connect(delayR);
        feedR.out.connect(panR);
        panL.connect(wetGain);
        panR.connect(wetGain);
        wetGain.connect(outputGain);

      } else if (p.mode === 'pingpong') {
        // True cross-feed ping-pong: input→L→cross→R→cross→L
        const delayL = actx.createDelay(5.0);
        delayL.delayTime.value = baseTime;
        const delayR = actx.createDelay(5.0);
        delayR.delayTime.value = baseTime;
        addLFO(delayL, '_L');
        addLFO(delayR, '_R');

        const feedL = makeFeedPath('_L');
        const feedR = makeFeedPath('_R');

        const crossLR = actx.createGain();
        crossLR.gain.value = Math.min(p.feedback, 0.95);
        const crossRL = actx.createGain();
        crossRL.gain.value = Math.min(p.feedback, 0.95);
        internalNodes.delayL = delayL;
        internalNodes.delayR = delayR;
        internalNodes.crossGainLR = crossLR;
        internalNodes.crossGainRL = crossRL;

        const panL = actx.createStereoPanner();
        panL.pan.value = -1;
        const panR = actx.createStereoPanner();
        panR.pan.value = 1;
        internalNodes.panL = panL;
        internalNodes.panR = panR;

        inputHPF.connect(delayL);
        delayL.connect(feedL.in);
        feedL.out.connect(panL);         // wet output left
        feedL.out.connect(crossLR);      // cross-feed to right
        crossLR.connect(delayR);
        delayR.connect(feedR.in);
        feedR.out.connect(panR);         // wet output right
        feedR.out.connect(crossRL);      // cross-feed back to left
        crossRL.connect(delayL);
        panL.connect(wetGain);
        panR.connect(wetGain);
        wetGain.connect(outputGain);
      }

      }

    } else if (slot.type === 'saturator') {
      const drive = p.drive || 0.3;
      const preGain = actx.createGain();
      preGain.gain.value = 1 + drive * 5;
      const shaper = actx.createWaveShaper();
      shaper.oversample = '4x';
      shaper.curve = makeSatCurve(drive, p.character || 'soft');
      const k = drive * 12 + 1;
      const postGain = actx.createGain();
      postGain.gain.value = Math.tanh(k) / k;
      const dcBlocker = actx.createBiquadFilter();
      dcBlocker.type = 'highpass';
      dcBlocker.frequency.value = 20;
      dcBlocker.Q.value = 0.5;
      const toneFilter = actx.createBiquadFilter();
      toneFilter.type = 'highshelf';
      toneFilter.frequency.value = 3000;
      toneFilter.gain.value = ((p.tone ?? 0.5) - 0.5) * 16;
      const wetGain = actx.createGain();
      wetGain.gain.value = p.wet;
      const dryGain = actx.createGain();
      dryGain.gain.value = 1 - p.wet;
      internalNodes.preGain = preGain;
      internalNodes.shaper = shaper;
      internalNodes.postGain = postGain;
      internalNodes.dcBlocker = dcBlocker;
      internalNodes.toneFilter = toneFilter;
      internalNodes.wetGain = wetGain;
      internalNodes.dryGain = dryGain;
      inputGain.connect(preGain);
      preGain.connect(shaper);
      shaper.connect(postGain);
      postGain.connect(dcBlocker);
      dcBlocker.connect(toneFilter);
      toneFilter.connect(wetGain);
      wetGain.connect(outputGain);
      inputGain.connect(dryGain);
      dryGain.connect(outputGain);

    } else if (slot.type === 'amp') {
      const ampGraph = await createAmpGraph(actx, p);
      internalNodes.ampGraph = ampGraph;
      inputGain.connect(ampGraph.input);
      ampGraph.output.connect(outputGain);
    } else if (slot.type === 'vst') {
      // ─── VST3 bridge — AudioWorkletNode that round-trips audio via WebSocket ──
      // The worklet is loaded lazily; if the bridge is not running the node passes
      // silence. Safe to include in the chain even before the bridge starts.

      // A bridged plugin runs in real time in another process; it cannot render offline.
      if (p.pluginId && !targetContext) {
        console.log('[VST] buildEffectSubgraph: loading pluginId=', p.pluginId);
        try {
          // Dynamic import so the rest of the app compiles without the bridge
          const { VstBridgeNode } = await import('../audio/VstBridgeNode');
          const vstNode = new VstBridgeNode(actx);
          console.log('[VST] calling vstNode.load...');
          await vstNode.load(p.pluginId, { numChannels: 2, blockSize: 128 });
          console.log('[VST] load succeeded, instanceId=', vstNode.instanceId, 'params=', vstNode.params.length);
          vstNode.connect(outputGain);
          vstNode.connectFrom(inputGain);
          internalNodes.vstNode = vstNode;
          // Also expose the raw AudioWorkletNode so the FX chain can be rewired
          internalNodes.vstWorklet = vstNode.node;
        } catch (err) {
          console.error('[VST] load FAILED:', err.message, err);
          inputGain.connect(outputGain); // passthrough on error
        }
      } else {
        // No plugin selected yet — transparent passthrough
        inputGain.connect(outputGain);
      }
    }

    return { inputGain, outputGain, internalNodes };
  }, [audioContext, bpm, getCachedIR]);

  // Offline export asks for one channel's insert chain in its own context.
  useEffect(() => {
    if (!registerOfflineFxBuilder) return undefined;
    registerOfflineFxBuilder(async (offlineContext, slots) => {
      const enabled = (Array.isArray(slots) ? slots : []).filter((slot) => slot?.enabled && slot.params);
      if (enabled.length === 0) return null;
      const entries = [];
      try {
        for (const slot of enabled) {
          // eslint-disable-next-line no-await-in-loop
          entries.push(await buildEffectSubgraph(slot, offlineContext));
        }
      } catch (error) { entries.forEach(teardownFxNodes); throw error; }
      for (let index = 0; index < entries.length - 1; index += 1) {
        entries[index].outputGain.connect(entries[index + 1].inputGain);
      }
      return { input: entries[0].inputGain, output: entries[entries.length - 1].outputGain };
    });
    return () => registerOfflineFxBuilder(null);
  }, [buildEffectSubgraph, registerOfflineFxBuilder]);

  // ─── Rebuild full FX chain for a track ────────────────────────────────────

  const rebuildFxChain = useCallback(async (trackId, slots) => {
    // Cancel any in-flight rebuild for this track — prevents parallel audio paths
    if (rebuildControlRef.current[trackId]) {
      rebuildControlRef.current[trackId].cancelled = true;
    }
    const control = { cancelled: false };
    rebuildControlRef.current[trackId] = control;

    if (!trackNodesRef || !audioContext) return;
    const trackNodes = trackNodesRef.current.get(trackId);
    if (!trackNodes) return;
    const { fxInputGain, fxOutputGain } = trackNodes;
    if (!fxInputGain || !fxOutputGain) return;

    // Put the original direct connection behind a gate once. From then on all
    // topology edits crossfade between two complete graphs; the live graph is
    // never disconnected while an async worklet/IR/plugin is still loading.
    let prev = fxNodesMapRef.current.get(trackId);
    if (prev && prev.sourceInput !== fxInputGain) {
      teardownFxChain(prev);
      fxNodesMapRef.current.delete(trackId);
      prev = null;
    }
    if (!prev) {
      const bypassGate = audioContext.createGain();
      bypassGate.gain.value = 1;
      try { fxInputGain.disconnect(fxOutputGain); } catch (_) {
        try { fxInputGain.disconnect(); } catch (_) {}
      }
      fxInputGain.connect(bypassGate);
      bypassGate.connect(fxOutputGain);
      prev = {
        slots: [],
        sourceInput: fxInputGain,
        inputTarget: bypassGate,
        outputGate: bypassGate
      };
      fxNodesMapRef.current.set(trackId, prev);
    }

    const enabled = (slots || []).filter(s => s.enabled);
    const built = await Promise.allSettled(enabled.map(async slot => {
      const { inputGain, outputGain, internalNodes } = await buildEffectSubgraph(slot);
      return { slotId: slot.id, type: slot.type, inputGain, outputGain, internalNodes };
    }));
    const entries = built.filter(result => result.status === 'fulfilled').map(result => result.value);
    const failed = built.find(result => result.status === 'rejected');
    if (failed) { entries.forEach(teardownFxNodes); throw failed.reason; }

    // A newer rebuild was triggered while we were awaiting — discard these nodes
    if (control.cancelled) {
      entries.forEach(e => teardownFxNodes(e));
      return;
    }

    const outputGate = audioContext.createGain();
    outputGate.gain.value = 0;
    let inputTarget = outputGate;
    if (entries.length === 0) {
      fxInputGain.connect(outputGate);
    } else {
      inputTarget = entries[0].inputGain;
      fxInputGain.connect(inputTarget);
      for (let i = 0; i < entries.length - 1; i++) {
        entries[i].outputGain.connect(entries[i + 1].inputGain);
      }
      entries[entries.length - 1].outputGain.connect(outputGate);
    }
    outputGate.connect(fxOutputGain);

    const nextChain = {
      slots: entries,
      sourceInput: fxInputGain,
      inputTarget,
      outputGate,
      savedSignature: JSON.stringify(slots || [])
    };
    fxNodesMapRef.current.set(trackId, nextChain);
    // Record the key against the backend that was actually created. This keeps
    // worklet controls tail-preserving while allowing the fallback's true
    // topology changes to rebuild safely.
    fxStructureRef.current[trackId] = getStructureKey(slots, nextChain);

    const now = audioContext.currentTime;
    const fadeEnd = now + FX_GRAPH_CROSSFADE_SECONDS;
    rampFxGate(prev.outputGate?.gain, 0, now, fadeEnd);
    rampFxGate(outputGate.gain, 1, now, fadeEnd);
    prev.cleanupTimer = window.setTimeout(() => {
      teardownFxChain(prev);
    }, Math.ceil((FX_GRAPH_CROSSFADE_SECONDS + 0.01) * 1000));

    // Report the chain's deterministic latency (VST bridge prebuffer) so the
    // transport scheduler can delay-compensate this track.
    const chainLatency = entries.reduce((total, entry) => (
      total + (entry.internalNodes?.vstNode?.latencySeconds || 0) + (entry.internalNodes?.ampGraph?.latencySeconds || 0)
    ), 0);
    registerTrackFxLatency?.(trackId, chainLatency);
  }, [audioContext, trackNodesRef, buildEffectSubgraph, registerTrackFxLatency]);

  // Share in-flight graph work with the chat player so readiness means the
  // effects are attached to the current channel, not merely present in state.
  const prepareFxChain = useCallback((trackId, slots) => {
    const source = trackNodesRef.current.get(trackId)?.fxInputGain;
    const signature = JSON.stringify(slots || []);
    const pending = pendingFxRef.current.get(trackId);
    if (pending?.source === source && pending.signature === signature) return pending.promise;
    const hasAmp = slots.some(slot => slot.enabled && slot.type === 'amp');
    if (hasAmp) setFxChainStatus(prev => ({ ...prev, [trackId]: { status: 'loading' } }));
    const promise = rebuildFxChain(trackId, slots);
    const entry = { source, signature, promise };
    pendingFxRef.current.set(trackId, entry);
    if (hasAmp) promise.then(() => {
      if (pendingFxRef.current.get(trackId) === entry) setFxChainStatus(prev => ({ ...prev, [trackId]: { status: 'ready' } }));
    }, error => {
      if (pendingFxRef.current.get(trackId) === entry) setFxChainStatus(prev => ({ ...prev, [trackId]: { status: 'error', message: error.message } }));
    });
    promise.finally(() => {
      if (pendingFxRef.current.get(trackId) === entry) pendingFxRef.current.delete(trackId);
    }).catch(() => {});
    return promise;
  }, [trackNodesRef, rebuildFxChain]);

  const waitForPlaybackReady = useCallback(async (snapshot) => {
    const channels = snapshot ? [...(snapshot.tracks || []), ...(snapshot.buses || [])] : savedChannelsRef.current;
    await Promise.all(channels.map(async channel => {
      const slots = channel.fx || [];
      const source = trackNodesRef.current.get(channel.id)?.fxInputGain;
      if (!source) {
        if (slots.some(slot => slot.enabled)) throw new Error(`Effects for ${channel.name || channel.id} are not ready.`);
        return;
      }
      const live = fxNodesMapRef.current.get(channel.id);
      const ampError = live?.slots?.find(entry => entry.internalNodes?.ampGraph?.error)?.internalNodes.ampGraph.error;
      if (live?.sourceInput === source && live.savedSignature === JSON.stringify(slots)) {
        if (ampError) throw ampError;
        return;
      }
      await prepareFxChain(channel.id, slots);
      const connected = fxNodesMapRef.current.get(channel.id);
      if (slots.some(slot => slot.enabled) && (connected?.sourceInput !== source || connected.savedSignature !== JSON.stringify(slots))) {
        throw new Error(`Effects for ${channel.name || channel.id} could not be connected. Refresh and retry.`);
      }
    }));
  }, [trackNodesRef, prepareFxChain]);

  // ─── Sync FX state from tracks (session load) ──────────────────────────────

  useEffect(() => {
    if (!tracks || !audioContext) return;
    [...tracks, ...(drumTracks || []), ...(buses || [])].forEach(track => {
      const trackFx = track.fx || [];
      const currentFx = trackFxMapRef.current[track.id] || [];
      if (JSON.stringify(trackFx) !== JSON.stringify(currentFx)) {
        fxStructureRef.current[track.id] = null;
        setTrackFxMap(prev => ({ ...prev, [track.id]: trackFx }));
      }
    });
  }, [tracks, drumTracks, buses, audioContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Rebuild chains only when topology changes (not on every param tweak) ──
  // Normal param changes go through applyParamToNode; rebuilding on every knob
  // move tears down and recreates the audio graph, causing dropouts and parallel
  // signal paths (the out-of-tempo doubling artifact).

  useEffect(() => {
    if (!audioContext) return;
    Object.entries(trackFxMap).forEach(([trackId, slots]) => {
      const structKey = getStructureKey(slots, fxNodesMapRef.current.get(trackId));
      const live = fxNodesMapRef.current.get(trackId);
      const source = trackNodesRef.current.get(trackId)?.fxInputGain;
      if (source && (fxStructureRef.current[trackId] !== structKey || live?.sourceInput !== source)) {
        prepareFxChain(trackId, slots).catch(error => console.error('Effect graph failed:', error));
      }
    });
  }, [trackFxMap, audioContext, prepareFxChain, trackNodesRef, tracks, drumTracks, buses]);

  // Tempo-synced echoes follow the transport without a rebuild. The worklet
  // holds its own delay lines, so retuning is a message rather than a new
  // graph, and the repeats already in flight keep playing.
  useEffect(() => {
    Object.entries(trackFxMapRef.current).forEach(([trackId, slots]) => {
      const chain = fxNodesMapRef.current.get(trackId);
      if (!chain?.slots) return;
      (slots || []).forEach((slot) => {
        if (slot.type !== 'delay' || !slot.params?.syncToTempo) return;
        const live = chain.slots.find((entry) => entry.slotId === slot.id);
        const echoNode = live?.internalNodes?.echoNode;
        if (echoNode) {
          sendDelayParams(echoNode, buildEchoParams(slot.params, bpm));
        } else if (live) {
          // Tempo is not part of stored FX state. Retune native fallback delay
          // nodes explicitly so synced repeats continue to follow the session.
          applyParamToNode(live, '__tempo__', bpm, audioContext, bpm, slot.params);
        }
      });
    });
  }, [bpm, audioContext]);

  // ─── Clean up removed tracks ───────────────────────────────────────────────

  useEffect(() => {
    if (!tracks) return;
    const trackIds = new Set([...tracks, ...(drumTracks || []), ...(buses || [])].map(t => t.id));
    setTrackFxMap(prev => {
      const next = { ...prev };
      let changed = false;
      for (const id of Object.keys(next)) {
        if (!trackIds.has(id)) {
          const entry = fxNodesMapRef.current.get(id);
          teardownFxChain(entry);
          fxNodesMapRef.current.delete(id);
          if (rebuildControlRef.current[id]) rebuildControlRef.current[id].cancelled = true;
          delete fxStructureRef.current[id];
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [tracks, drumTracks, buses]);

  // ─── Public actions ────────────────────────────────────────────────────────

  const addEffect = useCallback((trackId, type) => {
    const slot = {
      id: newFxId(),
      type,
      enabled: true,
      params: JSON.parse(JSON.stringify(DEFAULT_PARAMS[type] || {}))
    };
    setTrackFxMap(prev => {
      const current = prev[trackId] || [];
      const newSlots = [...current, slot];
      updateTrack(trackId, { fx: newSlots });
      return { ...prev, [trackId]: newSlots };
    });
  }, [updateTrack]);

  // Instrument controls and the mixer edit the same saved slot. Repeated On
  // clicks never create a second amp; bypass retains the user's settings.
  const setInstrumentAmpEnabled = useCallback((trackId, enabled, model = 'guitar-clean') => {
    setTrackFxMap(prev => {
      const current = prev[trackId] || savedChannelsRef.current.find(track => track.id === trackId)?.fx || [];
      const existing = current.find(slot => slot.type === 'amp');
      if (!existing && !enabled) return prev;
      const slots = existing ? current.map(slot => slot.id === existing.id ? { ...slot, enabled } : slot)
        : [{ id: newFxId(), type: 'amp', enabled: true, params: { ...AMP_DEFAULTS, model } }, ...current];
      updateTrack(trackId, { fx: slots });
      return { ...prev, [trackId]: slots };
    });
  }, [updateTrack]);

  const removeEffect = useCallback((trackId, slotId) => {
    setTrackFxMap(prev => {
      const newSlots = (prev[trackId] || []).filter(s => s.id !== slotId);
      updateTrack(trackId, { fx: newSlots });
      return { ...prev, [trackId]: newSlots };
    });
  }, [updateTrack]);

  const toggleEffect = useCallback((trackId, slotId) => {
    setTrackFxMap(prev => {
      const newSlots = (prev[trackId] || []).map(s =>
        s.id === slotId ? { ...s, enabled: !s.enabled } : s
      );
      updateTrack(trackId, { fx: newSlots });
      return { ...prev, [trackId]: newSlots };
    });
  }, [updateTrack]);

  const reorderEffects = useCallback((trackId, fromIndex, toIndex) => {
    setTrackFxMap(prev => {
      const slots = [...(prev[trackId] || [])];
      const [moved] = slots.splice(fromIndex, 1);
      slots.splice(toIndex, 0, moved);
      updateTrack(trackId, { fx: slots });
      return { ...prev, [trackId]: slots };
    });
  }, [updateTrack]);

  const replaceEffectParams = useCallback((trackId, slotId, newParams) => {
    setTrackFxMap(prev => {
      const slots = (prev[trackId] || []).map(s =>
        s.id === slotId ? { ...s, params: JSON.parse(JSON.stringify(newParams)) } : s
      );
      updateTrack(trackId, { fx: slots });
      // Invalidate structure key so the rebuild effect fires and audio nodes update
      fxStructureRef.current[trackId] = null;
      return { ...prev, [trackId]: slots };
    });
  }, [updateTrack]);

  const updateEffectParam = useCallback((trackId, slotId, dotPath, value) => {
    setTrackFxMap(prev => {
      const slots = (prev[trackId] || []).map(s => {
        if (s.id !== slotId) return s;
        return { ...s, params: deepSet(s.params, dotPath, value) };
      });
      updateTrack(trackId, { fx: slots });

      // Live-update the audio node
      const fxNodes = fxNodesMapRef.current.get(trackId);
      if (fxNodes && fxNodes.slots) {
        const entry = fxNodes.slots.find(e => e.slotId === slotId);
        const updatedSlot = slots.find(s => s.id === slotId);
        if (entry && updatedSlot) {
          applyParamToNode(entry, dotPath, value, audioContext, bpm, updatedSlot.params);
        }
      }

      return { ...prev, [trackId]: slots };
    });
  }, [updateTrack, audioContext, bpm]);

  // ─── Live param application ────────────────────────────────────────────────

  function applyParamToNode(entry, dotPath, value, actx, currentBpm, updatedParams) {
    if (!entry || !actx) return;
    const now = actx.currentTime;
    const { type, internalNodes } = entry;

    if (type === 'eq') {
      const parts = dotPath.split('.');

      // Top-level outputGain
      if (parts[0] === 'outputGain' && internalNodes.eqOutputGain) {
        internalNodes.eqOutputGain.gain.setTargetAtTime(Math.pow(10, value / 20), now, 0.01);
        return;
      }

      if (parts[0] !== 'bands') return;
      const bandIdx = parseInt(parts[1], 10);
      const prop = parts[2];
      // Collect all filters for this band
      const filters = [];
      for (let j = 0; j < 4; j++) {
        const node = internalNodes[`band_${bandIdx}_filter_${j}`];
        if (node) filters.push(node);
      }
      if (filters.length === 0) return;

      if (prop === 'freq') {
        filters.forEach(f => f.frequency.setTargetAtTime(value, now, 0.01));
      } else if (prop === 'gain') {
        filters.forEach(f => {
          if (['peaking', 'lowshelf', 'highshelf', 'notch'].includes(f.type)) {
            f.gain.setTargetAtTime(value, now, 0.01);
          }
        });
      } else if (prop === 'q') {
        filters.forEach(f => f.Q.setTargetAtTime(value, now, 0.01));
      } else if (prop === 'enabled') {
        const band = updatedParams?.bands?.[bandIdx];
        if (!band) return;
        if (value) {
          // Re-enable: restore params
          filters.forEach(f => {
            f.frequency.setTargetAtTime(band.freq, now, 0.01);
            if (['peaking', 'lowshelf', 'highshelf', 'notch'].includes(band.type)) {
              f.gain.setTargetAtTime(band.gain, now, 0.01);
            }
          });
        } else {
          // Bypass
          filters.forEach(f => {
            if (band.type === 'highpass') {
              f.frequency.setTargetAtTime(1, now, 0.01);
            } else if (band.type === 'lowpass') {
              f.frequency.setTargetAtTime(22050, now, 0.01);
            } else if (band.type === 'bandpass') {
              // bandpass bypass: push to sub-bass so it blocks everything audible
              f.frequency.setTargetAtTime(1, now, 0.01);
            } else {
              f.gain.setTargetAtTime(0, now, 0.01);
            }
          });
        }
      }
      // type/slope → rebuild via useEffect

    } else if (type === 'compressor') {
      const { la2aNode, comp1, comp2, makeupGain, preHPF, tubeSat, warmEQ, parallelDryGain } = internalNodes;
      if (la2aNode) {
        sendLa2aParams(la2aNode, updatedParams);
        return;
      }
      if (dotPath === 'peakReduction') {
        const PR = value;
        const isLimit = updatedParams?.mode === 'limit';
        if (comp1) comp1.threshold.setTargetAtTime(isLimit ? -12 * PR : -24 * PR, now, 0.01);
        if (comp2) comp2.threshold.setTargetAtTime(isLimit ? -6 * PR : -18 * PR, now, 0.01);
        if (tubeSat) {
          const drive = isLimit ? 0.05 + PR * 0.07 : 0.03 + PR * 0.09;
          tubeSat.curve = makeTubeCurve(drive);
        }
      } else if (dotPath === 'outputGain' && makeupGain) {
        const blend = updatedParams?.blend ?? 0;
        makeupGain.gain.setTargetAtTime((1 - blend) * Math.pow(10, value / 20), now, 0.01);
      } else if (dotPath === 'blend') {
        if (parallelDryGain) parallelDryGain.gain.setTargetAtTime(value, now, 0.01);
        if (makeupGain) {
          const og = updatedParams?.outputGain ?? 0;
          makeupGain.gain.setTargetAtTime((1 - value) * Math.pow(10, og / 20), now, 0.01);
        }
      } else if (dotPath === 'mode') {
        const isLimit = value === 'limit';
        const PR = updatedParams?.peakReduction ?? 0.3;
        if (preHPF) preHPF.frequency.setTargetAtTime(isLimit ? 120 : 72, now, 0.01);
        if (comp1) {
          comp1.threshold.setTargetAtTime(isLimit ? -12 * PR : -24 * PR, now, 0.01);
          comp1.ratio.setTargetAtTime(isLimit ? 20 : 3, now, 0.01);
          comp1.knee.setTargetAtTime(isLimit ? 2 : 8, now, 0.01);
          comp1.attack.setTargetAtTime(isLimit ? 0.003 : 0.008, now, 0.01);
          comp1.release.setTargetAtTime(isLimit ? 0.060 : 0.120, now, 0.01);
        }
        if (comp2) {
          comp2.threshold.setTargetAtTime(isLimit ? -6 * PR : -18 * PR, now, 0.01);
          comp2.ratio.setTargetAtTime(isLimit ? 6 : 1.5, now, 0.01);
          comp2.knee.setTargetAtTime(isLimit ? 1 : 6, now, 0.01);
          comp2.attack.setTargetAtTime(isLimit ? 0.015 : 0.080, now, 0.01);
          comp2.release.setTargetAtTime(isLimit ? 0.300 : 0.800, now, 0.01);
        }
        if (tubeSat) {
          const drive = isLimit ? 0.05 + PR * 0.07 : 0.03 + PR * 0.09;
          tubeSat.curve = makeTubeCurve(drive);
        }
        if (warmEQ) warmEQ.gain.setTargetAtTime(isLimit ? 0.5 : 0.8, now, 0.01);
      }

    } else if (type === 'reverb') {
      const { reverbNode } = internalNodes;
      if (reverbNode) {
        // Every reverb control is live in the worklet, including size and
        // decay, which previously forced a graph rebuild and a dropout.
        sendReverbParams(reverbNode, updatedParams);
        return;
      }
      const { wetGain, dryGain, hfDampFilter, osc_mod, lfoGain, preDelayNode } = internalNodes;
      if (dotPath === 'mix') {
        if (wetGain) wetGain.gain.setTargetAtTime(value, now, 0.01);
        if (dryGain) dryGain.gain.setTargetAtTime(1 - value, now, 0.01);
      } else if (dotPath === 'hfDamp' && hfDampFilter) {
        hfDampFilter.frequency.setTargetAtTime(value, now, 0.01);
      } else if (dotPath === 'preDelay' && preDelayNode) {
        preDelayNode.delayTime.setTargetAtTime(Math.max(0, value) / 1000, now, 0.01);
      } else if (dotPath === 'mod') {
        if (osc_mod) osc_mod.frequency.setTargetAtTime(0.4 + value * 1.8, now, 0.01);
        if (lfoGain) lfoGain.gain.setTargetAtTime(value * 0.004, now, 0.01);
      }
      // mode/size/decay → IR rebuild via structureKey

    } else if (type === 'delay') {
      const { echoNode } = internalNodes;
      if (echoNode) {
        // Style, mode and sync all retune the worklet in place; none of them
        // rebuilds the graph any more.
        sendDelayParams(echoNode, buildEchoParams(updatedParams, currentBpm));
        return;
      }
      const { wetGain, dryGain, delay, delayL, delayR,
              feedbackGain, feedbackGainL, feedbackGainR,
              crossGainLR, crossGainRL,
              feedLPF, feedLPF_L, feedLPF_R,
              inputHPF, panL, panR, haasDelay,
              lfoGain, lfoGain_L, lfoGain_R,
              lfoFlutterGain, lfoFlutterGain_L, lfoFlutterGain_R } = internalNodes;
      const cp = updatedParams;
      const CHAR_LPF_MAX = { digital: 19000, clean: 19000, tape: 7500, analog: 5500, lofi: 3000, telephone: 3400, fuzz: 4200 };
      const CHAR_HPF = { digital: 20, clean: 20, tape: 120, analog: 150, lofi: 200, telephone: 300, fuzz: 80 };
      const CHAR_LFO_DEPTH = { digital: 0, clean: 0, tape: 0.0012, analog: 0.0004, lofi: 0.0020, telephone: 0.0001, fuzz: 0.0006 };

      if (dotPath === 'mix') {
        if (wetGain) wetGain.gain.setTargetAtTime(value, now, 0.01);
        if (dryGain) dryGain.gain.setTargetAtTime(1 - value, now, 0.01);
      } else if (dotPath === 'feedback') {
        const fb = Math.min(value, 0.95);
        if (feedbackGain) feedbackGain.gain.setTargetAtTime(fb, now, 0.01);
        if (feedbackGainL) feedbackGainL.gain.setTargetAtTime(fb, now, 0.01);
        if (feedbackGainR) feedbackGainR.gain.setTargetAtTime(fb * 0.9, now, 0.01);
        if (crossGainLR) crossGainLR.gain.setTargetAtTime(fb, now, 0.01);
        if (crossGainRL) crossGainRL.gain.setTargetAtTime(fb, now, 0.01);
      } else if (['time', 'syncToTempo', 'noteValue', 'groove', '__tempo__'].includes(dotPath)) {
        const { timeLeftMs, timeRightMs } = resolveDelayTimes(cp, currentBpm);
        const leftSeconds = timeLeftMs / 1000;
        const rightSeconds = timeRightMs / 1000;
        if (delay) delay.delayTime.setTargetAtTime(leftSeconds, now, 0.02);
        if (delayL) delayL.delayTime.setTargetAtTime(leftSeconds, now, 0.02);
        if (delayR) delayR.delayTime.setTargetAtTime(rightSeconds, now, 0.02);
      } else if (dotPath === 'tone') {
        const char = cp ? cp.character : 'tape';
        const lpfMax = CHAR_LPF_MAX[char] || 18000;
        const hpfFloor = CHAR_HPF[char] || 80;
        const toneMin = hpfFloor * 2;
        const cutoff = toneMin * Math.pow(lpfMax / toneMin, value);
        if (feedLPF) feedLPF.frequency.setTargetAtTime(cutoff, now, 0.01);
        if (feedLPF_L) feedLPF_L.frequency.setTargetAtTime(cutoff, now, 0.01);
        if (feedLPF_R) feedLPF_R.frequency.setTargetAtTime(cutoff, now, 0.01);
      } else if (dotPath === 'hpf' && inputHPF) {
        const freq = 20 * Math.pow(25, value);
        inputHPF.frequency.setTargetAtTime(freq, now, 0.01);
      } else if (dotPath === 'spread' || dotPath === 'width') {
        const s = Math.max(0, Math.min(1, value));
        if (panL) panL.pan.setTargetAtTime(-s, now, 0.01);
        if (panR) panR.pan.setTargetAtTime(s, now, 0.01);
        if (haasDelay) haasDelay.delayTime.setTargetAtTime(s * 0.012, now, 0.01);
        // dual mode pans
        if (cp && cp.mode === 'dual') {
          if (panL) panL.pan.setTargetAtTime(-(0.5 + s * 0.5), now, 0.01);
          if (panR) panR.pan.setTargetAtTime((0.5 + s * 0.5), now, 0.01);
        }
      } else if (dotPath === 'wowFlutter') {
        const style = resolveEchoStyle(cp?.character);
        const depth = (CHAR_LFO_DEPTH[style] || 0) * Math.max(0, Math.min(1, value));
        [lfoGain, lfoGain_L, lfoGain_R].forEach(node => {
          if (node) node.gain.setTargetAtTime(depth, now, 0.01);
        });
        [lfoFlutterGain, lfoFlutterGain_L, lfoFlutterGain_R].forEach(node => {
          if (node) node.gain.setTargetAtTime(depth * 0.15, now, 0.01);
        });
      }
      // mode/character/syncToTempo/noteValue → rebuild via structureKey

    } else if (type === 'saturator') {
      const { shaper, wetGain, dryGain, preGain, postGain, toneFilter } = internalNodes;
      if (dotPath === 'wet') {
        if (wetGain) wetGain.gain.setTargetAtTime(value, now, 0.01);
        if (dryGain) dryGain.gain.setTargetAtTime(1 - value, now, 0.01);
      } else if (dotPath === 'drive') {
        if (shaper) shaper.curve = makeSatCurve(value, updatedParams?.character || 'soft');
        if (preGain) preGain.gain.setTargetAtTime(1 + value * 5, now, 0.01);
        if (postGain) { const k = value * 12 + 1; postGain.gain.setTargetAtTime(Math.tanh(k) / k, now, 0.01); }
      } else if (dotPath === 'tone') {
        if (toneFilter) toneFilter.gain.setTargetAtTime((value - 0.5) * 16, now, 0.01);
      }
      // character → rebuild via useEffect
    } else if (type === 'amp') {
      // Capture changes build a fresh graph and crossfade; tone/level/mix
      // controls only retune native AudioParams on the existing graph.
      if (dotPath !== 'model') internalNodes.ampGraph?.setParams(updatedParams);
    } else if (type === 'vst') {
      const { vstNode } = internalNodes;
      if (!vstNode) return;

      if (dotPath === 'pluginId') {
        // Plugin change → structureKey changes → full rebuild (handled above)
      } else if (dotPath.startsWith('vstParam.')) {
        const paramIndex = parseInt(dotPath.split('.')[1], 10);
        if (!isNaN(paramIndex)) vstNode.setParameter(paramIndex, value);
      }
    }
  }

  // ─── Post-EQ analyser access ───────────────────────────────────────────────

  const getEqPostAnalyser = useCallback((trackId, slotId) => {
    const fxNodes = fxNodesMapRef.current.get(trackId);
    if (!fxNodes?.slots) return null;
    const entry = fxNodes.slots.find(e => e.slotId === slotId);
    return entry?.internalNodes?.postAnalyser ?? null;
  }, []);

  // ─── Gain reduction meter (sum both opto stages) ──────────────────────────

  const getCompressorReduction = useCallback((trackId, slotId) => {
    const fxNodes = fxNodesMapRef.current.get(trackId);
    if (!fxNodes || !fxNodes.slots) return 0;
    const entry = fxNodes.slots.find(e => e.slotId === slotId);
    if (!entry || !entry.internalNodes) return 0;
    const { la2aMeter, comp1, comp2 } = entry.internalNodes;
    if (la2aMeter) return -Math.max(0, Number(la2aMeter.reductionDb) || 0);
    return (comp1?.reduction || 0) + (comp2?.reduction || 0);
  }, []);

  // ─── VST bridge node accessor ──────────────────────────────────────────────

  const getVstBridgeNode = useCallback((trackId, slotId) => {
    const fxNodes = fxNodesMapRef.current.get(trackId);
    if (!fxNodes?.slots) return null;
    const entry = fxNodes.slots.find(e => e.slotId === slotId);
    return entry?.internalNodes?.vstNode ?? null;
  }, []);

  // ─── Input level meter (reads inputAnalyser, returns peak dBFS) ───────────

  const getCompressorInputLevel = useCallback((trackId, slotId, buf) => {
    const fxNodes = fxNodesMapRef.current.get(trackId);
    if (!fxNodes?.slots) return -Infinity;
    const entry = fxNodes.slots.find(e => e.slotId === slotId);
    if (!entry?.internalNodes?.inputAnalyser) return -Infinity;
    entry.internalNodes.inputAnalyser.getFloatTimeDomainData(buf);
    let peak = 0;
    for (let i = 0; i < buf.length; i++) {
      const abs = Math.abs(buf[i]);
      if (abs > peak) peak = abs;
    }
    return peak > 0 ? 20 * Math.log10(peak) : -Infinity;
  }, []);

  // ─── Context value ─────────────────────────────────────────────────────────

  const value = {
    waitForPlaybackReady,
    trackFxMap,
    fxChainStatus,
    addEffect,
    setInstrumentAmpEnabled,
    removeEffect,
    toggleEffect,
    reorderEffects,
    updateEffectParam,
    replaceEffectParams,
    getCompressorReduction,
    getCompressorInputLevel,
    getEqPostAnalyser,
    getVstBridgeNode,
  };

  return <FxContext.Provider value={value}>{children}</FxContext.Provider>;
};
